/**
 * Allys Area Stats Component
 * 
 * Displays lifestyle highlights, weather data, and market trends
 * in a visually appealing card layout.
 */

'use client';

import { 
  Sun, 
  Wind, 
  Drop, 
  TrendUp, 
  ArrowUpRight,
  ChartLineUp,
  CircleNotch
} from '@phosphor-icons/react';
import type { AreaStatsProps, AreaPageMessages } from '@routeroyal/theme-core';
import { getCardinalDirection } from '@routeroyal/theme-core';

export interface AllysAreaStatsProps extends AreaStatsProps {
  /**
   * Translation messages
   */
  messages?: AreaPageMessages;
}

export function AllysAreaStats({
  area,
  weather,
  marketStats,
  weatherLoading = false,
  messages,
  className = '',
}: AllysAreaStatsProps) {
  // Default market data if not provided
  const defaultMarketData = [
    { year: '2019', price: 1.0, formatted: '$1.00M' },
    { year: '2020', price: 1.12, formatted: '$1.12M' },
    { year: '2021', price: 1.08, formatted: '$1.08M' },
    { year: '2022', price: 1.25, formatted: '$1.25M' },
    { year: '2023', price: 1.38, formatted: '$1.38M' },
    { year: '2024', price: 1.45, formatted: '$1.45M' },
  ];

  const priceHistory = marketStats?.priceHistory || defaultMarketData;
  const hasMarketData = priceHistory.length > 0;

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${className}`}>
      {/* Market Analysis Card */}
      <div 
        className="bg-white p-6 md:p-8 rounded-[2rem] shadow-[0_4px_20px_-4px_rgba(0,0,0,0.08)] hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.12)] border border-gray-100/80 flex flex-col h-full hover:-translate-y-1 transition-all duration-500"
      >
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full bg-[var(--color-primary)]"></span>
              <p 
                className="text-[10px] text-[var(--color-text-muted)] font-mono tracking-[0.2em] uppercase font-inter"
              >
                {messages?.marketIntelligence || 'Market Intelligence'}
              </p>
            </div>
            <h3 className="font-serif text-2xl md:text-3xl text-[var(--color-text)]">
              {messages?.propertyTrends || 'Property Trends'}
            </h3>
          </div>
          
          {hasMarketData && (
            <div className="hidden md:flex gap-1 bg-gray-50 p-1 rounded-full border border-gray-100 h-fit">
              {['1Y', '3Y', '5Y'].map((period) => (
                <button 
                  key={period}
                  className={`px-3 py-1 rounded-full text-[10px] font-bold tracking-wider transition-all duration-300 ${
                    period === '5Y' 
                      ? 'bg-[var(--color-text)] text-white shadow-md' 
                      : 'text-gray-400 hover:text-[var(--color-text)]'
                  }`}
                >
                  {period}
                </button>
              ))}
            </div>
          )}
        </div>

        {hasMarketData ? (
          <>
            {/* Simple Price Range Display */}
            <div className="flex-1 flex flex-col justify-center items-center py-8">
              <div className="text-center">
                <p 
                  className="text-sm text-[var(--color-text-muted)] mb-2 font-inter"
                >
                  Average Property Price
                </p>
                <p className="font-serif text-4xl md:text-5xl text-[var(--color-text)]">
                  {area.priceRange}
                </p>
              </div>
              
              {/* Trend indicator */}
              <div className="mt-6 flex items-center gap-2">
                <ChartLineUp size={24} className="text-[var(--color-primary)]" weight="duotone" />
                <span 
                  className="text-sm text-[var(--color-text-muted)] font-inter"
                >
                  Strong growth in luxury segment
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-8 pt-6 border-t border-gray-100 mt-auto">
              <div>
                <span 
                  className="text-gray-400 text-[10px] uppercase tracking-widest font-mono block mb-1 font-inter"
                >
                  {messages?.avgYield || 'Avg. Yield'}
                </span>
                <span className="text-[var(--color-text)] font-serif text-2xl">
                  {marketStats?.averageYield || '4.2%'}
                </span>
              </div>
              <div>
                <span 
                  className="text-gray-400 text-[10px] uppercase tracking-widest font-mono block mb-1 font-inter"
                >
                  {messages?.growth || 'Growth'} ({marketStats?.growth?.period || '5Y'})
                </span>
                <div className="flex items-center gap-3">
                  <span className="text-[var(--color-text)] font-serif text-2xl">
                    {marketStats?.growth?.percentage || '+45%'}
                  </span>
                  <span className="bg-green-50 text-green-700 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 border border-green-100">
                    <TrendUp size={10} weight="bold" />
                    High
                  </span>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8 bg-gray-50/50 rounded-3xl border border-dashed border-gray-200/80">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-gray-300 mb-4 shadow-sm">
              <ChartLineUp size={20} weight="light" />
            </div>
            <h4 className="font-serif text-lg text-[var(--color-text)] mb-2">
              Market Data Unavailable
            </h4>
            <p 
              className="text-sm text-gray-500 font-light max-w-xs leading-relaxed font-inter"
            >
              Detailed transaction history and valuation metrics are currently being aggregated for this region.
            </p>
          </div>
        )}
      </div>

      {/* Lifestyle & Weather Card */}
      <div 
        className="bg-white p-6 md:p-8 rounded-[2rem] shadow-[0_4px_20px_-4px_rgba(0,0,0,0.08)] hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.12)] border border-gray-100/80 flex flex-col h-full hover:-translate-y-1 transition-all duration-500"
      >
        <div className="mb-6 flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full bg-[var(--color-text)]"></span>
              <p 
                className="text-[10px] text-[var(--color-text-muted)] font-mono tracking-[0.2em] uppercase font-inter"
              >
                {messages?.lifestyleAudit || 'Lifestyle Audit'}
              </p>
            </div>
            <h3 className="font-serif text-2xl md:text-3xl text-[var(--color-text)]">
              {messages?.keyHighlights || 'Key Highlights'}
            </h3>
          </div>
          {weatherLoading && (
            <CircleNotch size={14} className="text-[var(--color-primary)] animate-spin mt-2" weight="bold" />
          )}
        </div>

        {/* Highlights List */}
        <ul className="space-y-4 mb-6 flex-1">
          {area.highlights.map((highlight, idx) => (
            <li 
              key={idx} 
              className="flex items-start gap-4 text-[var(--color-text)] group"
            >
              <span className="mt-1 w-6 h-6 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center text-[var(--color-primary)] group-hover:bg-[var(--color-primary)] group-hover:text-white transition-colors duration-300 shrink-0">
                <ArrowUpRight size={12} weight="bold" />
              </span>
              <span 
                className="font-light text-lg leading-relaxed border-b border-transparent group-hover:border-[var(--color-primary)]/20 transition-all font-inter"
              >
                {highlight}
              </span>
            </li>
          ))}
        </ul>

        {/* Weather Data */}
        <div className="mt-auto pt-6 border-t border-gray-100 grid grid-cols-3 gap-3">
          {/* Temperature */}
          <div className="text-center p-3 rounded-2xl bg-gray-50/50 border border-gray-100 group hover:border-[var(--color-primary)]/30 transition-colors">
            <Sun size={18} className="mx-auto mb-2 text-[var(--color-primary)] group-hover:scale-110 transition-transform" weight="duotone" />
            <span 
              className="block text-[10px] text-gray-400 uppercase tracking-wider mb-1 font-inter"
            >
              {messages?.temperature || 'Temp'}
            </span>
            <span 
              className="font-mono text-sm text-[var(--color-text)] font-bold font-inter"
            >
              {weather ? `${weather.temperature}°C` : '--'}
            </span>
          </div>
          
          {/* Wind */}
          <div className="text-center p-3 rounded-2xl bg-gray-50/50 border border-gray-100 group hover:border-[var(--color-primary)]/30 transition-colors">
            <Wind size={18} className="mx-auto mb-2 text-[var(--color-primary)] group-hover:scale-110 transition-transform" weight="duotone" />
            <span 
              className="block text-[10px] text-gray-400 uppercase tracking-wider mb-1 font-inter"
            >
              {messages?.wind || 'Wind'}
            </span>
            <span 
              className="font-mono text-sm text-[var(--color-text)] font-bold whitespace-nowrap font-inter"
            >
              {weather ? `${getCardinalDirection(weather.windDirection)} ${weather.windSpeed}km` : '--'}
            </span>
          </div>
          
          {/* Humidity */}
          <div className="text-center p-3 rounded-2xl bg-gray-50/50 border border-gray-100 group hover:border-[var(--color-primary)]/30 transition-colors">
            <Drop size={18} className="mx-auto mb-2 text-[var(--color-primary)] group-hover:scale-110 transition-transform" weight="duotone" />
            <span 
              className="block text-[10px] text-gray-400 uppercase tracking-wider mb-1 font-inter"
            >
              {messages?.humidity || 'Hum'}
            </span>
            <span 
              className="font-mono text-sm text-[var(--color-text)] font-bold font-inter"
            >
              {weather ? `${weather.humidity}%` : '--'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
